/** Marks a run still in flight. */
const WORKING_PREFIX = '⏳ ';
/** Marks a run that finished without the user seeing it. */
const UNSEEN_PREFIX = '🔔 ';
/** Every prefix this module owns, so one can be swapped for another. */
const PREFIXES = [WORKING_PREFIX, UNSEEN_PREFIX];

/**
 * What a workbench surface's run is doing, as far as the tab title is concerned.
 * `idle` withdraws the surface from the title entirely.
 */
export type AgentRunTitleState = 'working' | 'unseen' | 'idle';

/**
 * The live state of each reporting surface. Keyed so that several mounted workbench
 * panels aggregate into one title instead of overwriting each other.
 */
const statesByKey = new Map<string, Exclude<AgentRunTitleState, 'idle'>>();

/**
 * Watches for the title being rewritten under us. Present only while a prefix is
 * up, so an idle app carries no observer.
 */
let titleObserver: MutationObserver | undefined;

/**
 * The prefix the current set of reporters calls for. Work in flight outranks an
 * unseen result, since the run is the more current fact.
 *
 * @returns The prefix, or an empty string when the title should be left alone.
 */
function desiredPrefix(): string {
	const states = [...statesByKey.values()];
	if (states.includes('working')) {
		return WORKING_PREFIX;
	}
	if (states.includes('unseen')) {
		return UNSEEN_PREFIX;
	}
	return '';
}

/**
 * The title without any prefix this module may have put on it.
 *
 * @param title - Title to strip.
 * @returns The bare title.
 */
function stripPrefix(title: string): string {
	const prefix = PREFIXES.find((candidate) => title.startsWith(candidate));
	return prefix === undefined ? title : title.slice(prefix.length);
}

/**
 * Brings the document title in line with the reported states, preserving whatever
 * the rest of the app has set the title to. Idempotent, so it is safe to call in
 * response to its own writes.
 */
function applyPrefix(): void {
	const desired = `${desiredPrefix()}${stripPrefix(document.title)}`;
	if (document.title !== desired) {
		document.title = desired;
	}
}

/** Drops every unseen mark: the user is here, so the results are no longer unseen. */
function clearUnseen(): void {
	for (const [key, state] of statesByKey) {
		if (state === 'unseen') {
			statesByKey.delete(key);
		}
	}
	syncTitle();
}

/** Covers returning to a tab that was hidden rather than merely unfocused. */
function clearUnseenWhenVisible(): void {
	if (document.visibilityState === 'visible') {
		clearUnseen();
	}
}

/**
 * Starts re-asserting the prefix and listening for the user's return. Watching the
 * head rather than cooperating with whoever else writes the title keeps this
 * correct no matter what sets it — the router's head management included.
 */
function startWatching(): void {
	if (titleObserver !== undefined) {
		return;
	}
	titleObserver = new MutationObserver(applyPrefix);
	titleObserver.observe(document.head, { childList: true, subtree: true, characterData: true });
	globalThis.addEventListener('focus', clearUnseen);
	document.addEventListener('visibilitychange', clearUnseenWhenVisible);
}

/** Stops watching, leaving the title to the rest of the app. */
function stopWatching(): void {
	titleObserver?.disconnect();
	titleObserver = undefined;
	globalThis.removeEventListener('focus', clearUnseen);
	document.removeEventListener('visibilitychange', clearUnseenWhenVisible);
}

/** Writes the title and keeps the watcher alive for exactly as long as a prefix is up. */
function syncTitle(): void {
	applyPrefix();
	if (desiredPrefix() === '') {
		stopWatching();
	} else {
		startWatching();
	}
}

/**
 * Reports what one workbench surface's run is doing, and updates the tab title to suit.
 * The prefix survives navigation and any other rewrite of the title until the
 * reporter says otherwise; an unseen mark additionally clears itself once the user
 * comes back to the tab. Reporting `idle` withdraws the surface, which every
 * caller must do when it unmounts or the prefix outlives it.
 *
 * @param key - Identifies the reporting surface for the lifetime of its mount.
 * @param state - What that surface's run is doing.
 */
export function reportAgentRunTitleState(key: string, state: AgentRunTitleState): void {
	if (state === 'idle') {
		statesByKey.delete(key);
	} else {
		statesByKey.set(key, state);
	}
	syncTitle();
}
